import { useEffect, useMemo, useState } from "react";
import { useRouter } from "next/navigation";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Calendar, Hash, ToggleLeft, Type } from "lucide-react";

import BaseDialog from "@/components/Modals/BaseDialog";
import { Checkbox } from "@/components/shadcn/checkbox";
import { Input } from "@/components/shadcn/input";
import { NoxusInput } from "@/components/Form/InputField";
import { Label } from "@/components/shadcn/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/shadcn/select";
import { Skeleton } from "@/components/shadcn/skeleton";
import { SpotToast } from "@/components/SpotToast";
import { DataTableColumn, TableResponse, TablesService } from "@/generated";
import { DATA_KEYS, useRemoteData } from "@/utils/useRemoteData";
import { useSpotUser } from "@/utils/spotUser";
import { useDialogMutationLoading } from "@/hooks/useDialogMutationLoading";
import { extractErrorMessage } from "@/utils/plugins";
import { cn } from "@/lib/utils";
import { MAX_ENTITY_NAME } from "@/constants/entityLimits";

type ColType = "string" | "number" | "boolean" | "datetime";
type Delimiter = "," | ";" | "\t";
type Encoding = "utf-8" | "latin-1";

type DetectedColumn = {
  index: number; // original CSV column position (stable across include toggles)
  header: string; // original CSV header
  name: string; // snake_case identifier (editable)
  type: ColType;
  include: boolean;
};

const IDENTIFIER_RE = /^[a-z][a-z0-9_]*$/;
const HEADER_ROW_CANDIDATES = 20;

// ── CSV parser ────────────────────────────────────────────────────────────────

// Mirrors Python's csv.reader plus _drop_blank_rows, which the import uses.
function parseCsvRows(text: string, delimiter: Delimiter): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = "";
  let inQuotes = false;

  const endRow = () => {
    row.push(field);
    if (row.some((c) => c.trim() !== "")) rows.push(row);
    row = [];
    field = "";
  };

  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"' && text[i + 1] === '"') {
        field += '"';
        i++;
      } else if (c === '"') {
        inQuotes = false;
      } else {
        field += c;
      }
    } else if (c === '"' && field === "") {
      inQuotes = true;
    } else if (c === delimiter) {
      row.push(field);
      field = "";
    } else if (c === "\n" || c === "\r") {
      if (c === "\r" && text[i + 1] === "\n") i++;
      endRow();
    } else {
      field += c;
    }
  }
  if (field !== "" || row.length > 0) endRow();
  return rows;
}

// Pretty spreadsheets put title banners and notes above the real header;
// the densest of the first rows is almost always it (earliest on ties).
function detectHeaderRow(rows: string[][]): number {
  const limit = Math.min(rows.length, HEADER_ROW_CANDIDATES);
  let best = 0;
  let bestCount = -1;
  for (let i = 0; i < limit; i++) {
    const count = rows[i].filter((c) => c.trim() !== "").length;
    if (count > bestCount) {
      best = i;
      bestCount = count;
    }
  }
  return best;
}

function decodeBuffer(buffer: ArrayBuffer, encoding: Encoding): string {
  return new TextDecoder(
    encoding === "latin-1" ? "windows-1252" : "utf-8"
  ).decode(buffer);
}

function detectEncoding(buffer: ArrayBuffer): Encoding {
  try {
    new TextDecoder("utf-8", { fatal: true }).decode(buffer);
    return "utf-8";
  } catch {
    return "latin-1";
  }
}

function detectDelimiter(text: string): Delimiter {
  const newline = text.indexOf("\n");
  const line = newline === -1 ? text : text.slice(0, newline);
  const counts: Record<Delimiter, number> = { ",": 0, ";": 0, "\t": 0 };
  let inQuotes = false;
  for (const c of line) {
    if (c === '"') inQuotes = !inQuotes;
    else if (!inQuotes && c in counts) counts[c as Delimiter]++;
  }
  // Comma wins ties (the default); semicolon/tab only on a clear signal.
  let best: Delimiter = ",";
  for (const d of [";", "\t"] as const) {
    if (counts[d] > counts[best]) best = d;
  }
  return best;
}

function toSnakeCase(input: string): string {
  return input
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^[^a-z]+/, "")
    .replace(/_$/, "");
}

// Stricter than the backend's datetime.fromisoformat, so an inferred column never fails import.
const ISO_DATETIME_RE =
  /^(\d{4})-(\d{2})-(\d{2})(?:[T ](?:[01]\d|2[0-3]):[0-5]\d(?::[0-5]\d(?:\.\d{1,6})?)?(?:Z|[+-](?:[01]\d|2[0-3]):?[0-5]\d)?)?$/;

function isIsoDatetime(value: string): boolean {
  const match = ISO_DATETIME_RE.exec(value);
  if (!match) return false;
  const [year, month, day] = match.slice(1, 4).map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  return (
    date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day
  );
}

// A column named for an identifier is never summed, so its digits stay text:
// a tax id, a phone number, a postal code. The backend import keeps the same list.
const IDENTIFIER_WORDS = new Set([
  "id",
  "nif",
  "nipc",
  "vat",
  "tax",
  "phone",
  "tel",
  "mobile",
  "zip",
  "postal",
  "postcode",
  "iban",
  "swift",
  "bic",
  "ssn",
  "code",
]);

function isIdentifier(name: string, values: string[]): boolean {
  if (name.split("_").some((w) => IDENTIFIER_WORDS.has(w))) return true;
  return values.some((v) => v.startsWith("+") || /^0\d/.test(v));
}

function inferType(
  colIndex: number,
  rows: string[][],
  truncated: boolean,
  name: string
): ColType {
  const values = rows
    .map((r) => (r[colIndex] ?? "").trim())
    .filter((v) => v !== "");
  if (values.length === 0) return "string";
  if (isIdentifier(name, values)) return "string";
  if (values.every((v) => /^-?\d+(\.\d+)?$/.test(v))) return "number";
  if (values.every((v) => /^(true|false)$/i.test(v))) return "boolean";
  // A truncated .xlsx preview can't vouch for the rows it didn't see.
  if (!truncated && values.every(isIsoDatetime)) return "datetime";
  return "string";
}

// "id" is reserved (every table has an implicit id), so a re-imported export's
// id column is excluded by default — rename it to keep it.
function buildColumns(
  headers: string[],
  dataRows: string[][],
  truncated: boolean
): DetectedColumn[] {
  return headers.map((h, i) => {
    const name = toSnakeCase(h) || `column_${i + 1}`;
    return {
      index: i,
      header: h,
      name,
      type: inferType(i, dataRows, truncated, name),
      include: name !== "id",
    };
  });
}

const NOT_IMPORTED = "__not_imported__";

function autoMap(
  csv: DetectedColumn[],
  tableColumns: DataTableColumn[],
  overrides: Record<string, number | null>
): Record<string, number | null> {
  const taken = new Set(
    Object.values(overrides).filter((i): i is number => i !== null)
  );
  const byName = new Map<string, number>();
  for (const tc of tableColumns) {
    if (tc.name in overrides) continue;
    const names = [tc.name, tc.label]
      .filter((n): n is string => Boolean(n))
      .map((n) => n.trim().toLowerCase());
    const match = csv.find(
      (c) =>
        !taken.has(c.index) &&
        (c.name === tc.name || names.includes(c.header.trim().toLowerCase()))
    );
    if (match) {
      byName.set(tc.name, match.index);
      taken.add(match.index);
    }
  }
  const leftover = csv.filter((c) => !taken.has(c.index) && c.name !== "id");
  let next = 0;
  return Object.fromEntries(
    tableColumns.map((tc) => [
      tc.name,
      tc.name in overrides
        ? overrides[tc.name]
        : (byName.get(tc.name) ?? leftover[next++]?.index ?? null),
    ])
  );
}

const isXlsx = (file: File) => /\.xlsx$/i.test(file.name);

// ── Component ─────────────────────────────────────────────────────────────────

const TYPE_ICONS: Record<ColType, typeof Type> = {
  string: Type,
  number: Hash,
  boolean: ToggleLeft,
  datetime: Calendar,
};

export function ImportCsvDialog({
  file,
  isOpen,
  onOpenChange,
  table,
  onImported,
}: {
  file: File | null;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  table?: TableResponse;
  onImported?: () => void;
}) {
  const { spotGroup } = useSpotUser();
  const queryClient = useQueryClient();
  const router = useRouter();

  const [nameEdit, setNameEdit] = useState<string | null>(null);
  const [idType, setIdType] = useState<TableResponse["id_type"]>("uuid");
  const [columns, setColumns] = useState<DetectedColumn[]>([]);
  const [rows, setRows] = useState<string[][]>([]);
  const [parseError, setParseError] = useState<string | null>(null);
  const [progress, setProgress] = useState<string | null>(null);
  const [buffer, setBuffer] = useState<ArrayBuffer | null>(null);
  const [delimiter, setDelimiter] = useState<Delimiter>(",");
  const [encoding, setEncoding] = useState<Encoding>("utf-8");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewTruncated, setPreviewTruncated] = useState(false);
  const [totalRows, setTotalRows] = useState<number | null>(null);
  const [sheets, setSheets] = useState<string[]>([]);
  const [sheet, setSheet] = useState<string | null>(null);
  const [allRows, setAllRows] = useState<string[][]>([]);
  const [headerRow, setHeaderRow] = useState(0);
  const [mappingOverrides, setMappingOverrides] = useState<
    Record<string, number | null>
  >({});
  const [prevFile, setPrevFile] = useState(file);
  if (file !== prevFile) {
    setPrevFile(file);
    setNameEdit(null);
    setIdType("uuid");
    setMappingOverrides({});
  }

  // Read the file. CSV parses in-browser (with encoding + delimiter
  // auto-detection, both overridable); .xlsx goes through the server
  // preview endpoint since the browser can't read it.
  useEffect(() => {
    if (!file) return;
    setParseError(null);
    setProgress(null);
    setBuffer(null);
    setColumns([]);
    setRows([]);
    setAllRows([]);
    setHeaderRow(0);
    setSheets([]);
    setSheet(null);
    setTotalRows(null);
    setPreviewTruncated(false);
    if (isXlsx(file)) return;

    let cancelled = false;
    const reader = new FileReader();
    reader.onload = (e) => {
      if (cancelled) return;
      const buf = e.target?.result as ArrayBuffer;
      const detectedEncoding = detectEncoding(buf);
      setEncoding(detectedEncoding);
      setDelimiter(detectDelimiter(decodeBuffer(buf, detectedEncoding)));
      setBuffer(buf);
    };
    reader.onerror = () => setParseError("Failed to read the file.");
    reader.readAsArrayBuffer(file);
    return () => {
      cancelled = true;
    };
  }, [file]);

  const tableName =
    nameEdit ??
    table?.name ??
    (file
      ? file.name.replace(/\.(csv|xlsx)$/i, "").slice(0, MAX_ENTITY_NAME)
      : "");

  // .xlsx previews server-side (the browser can't parse it); re-runs when a
  // different sheet is picked.
  useEffect(() => {
    if (!file || !isXlsx(file)) return;
    let cancelled = false;
    setPreviewLoading(true);
    void (async () => {
      try {
        const res = await TablesService.importPreview({
          path: { group_id: spotGroup.id },
          query: sheet ? { sheet } : undefined,
          body: { file },
        });
        if (cancelled) return;
        setSheets(res.data.sheets ?? []);
        const previewRows = res.data.rows ?? [];
        if (previewRows.length === 0) {
          setParseError("Could not read any columns from this file.");
          return;
        }
        setParseError(null);
        // The server previews the first 50 rows only; the import itself
        // reads the whole file.
        setPreviewTruncated(previewRows.length >= 50);
        setTotalRows(res.data.total_rows ?? null);
        setAllRows(previewRows);
        setHeaderRow(detectHeaderRow(previewRows));
      } catch (e) {
        if (!cancelled)
          setParseError(extractErrorMessage(e, "Failed to read the file."));
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [file, sheet, spotGroup.id]);

  // (Re-)parse a CSV buffer on load and when the delimiter/encoding change.
  useEffect(() => {
    if (!buffer) return;
    const parsed = parseCsvRows(decodeBuffer(buffer, encoding), delimiter);
    if (parsed.length === 0) {
      setParseError("Could not read any columns from this file.");
      return;
    }
    setParseError(null);
    setAllRows(parsed);
    setHeaderRow(detectHeaderRow(parsed));
  }, [buffer, delimiter, encoding]);

  // Derive columns + data rows from the chosen header row.
  useEffect(() => {
    if (allRows.length === 0) return;
    const headers = allRows[headerRow] ?? [];
    if (headers.every((h) => !h.trim())) {
      setParseError("Could not read any columns from this file.");
      return;
    }
    setParseError(null);
    const dataRows = allRows.slice(headerRow + 1);
    setColumns(buildColumns(headers, dataRows, previewTruncated));
    setRows(dataRows);
  }, [allRows, headerRow, previewTruncated]);

  const appendMode = Boolean(table && table.columns.length > 0);
  const mapping = useMemo(
    () =>
      appendMode && table
        ? autoMap(columns, table.columns, mappingOverrides)
        : {},
    [appendMode, table, columns, mappingOverrides]
  );

  const mapped = Object.entries(mapping).filter(
    (entry): entry is [string, number] => entry[1] !== null
  );

  const importMutation = useMutation({
    mutationFn: async (): Promise<{
      tableId: string;
      inserted: number | null;
    }> => {
      if (appendMode && table) {
        if (!file) throw new Error("No file selected.");
        if (mapped.length === 0)
          throw new Error("Choose at least one column to fill.");
        setProgress("Importing…");
        const res = await TablesService.importCsvRows({
          path: { group_id: spotGroup.id, table_id: table.id },
          body: {
            file,
            spec: JSON.stringify({
              has_header: true,
              delimiter,
              encoding,
              sheet,
              skip_rows: headerRow,
              columns: mapped.map(([column, index]) => ({ index, column })),
            }),
          },
        });
        return { tableId: table.id, inserted: res.data.inserted };
      }
      const name = tableName.trim();
      if (!name) throw new Error("Table name is required.");
      if (!file) throw new Error("No file selected.");
      const included = columns.filter((c) => c.include);
      if (included.length === 0) throw new Error("Select at least one column.");

      // The backend parses the file and bulk-loads it (Postgres COPY) in one
      // atomic request — we upload the raw file plus the column spec.
      setProgress("Importing…");
      const spec = {
        name,
        has_header: true,
        delimiter,
        encoding,
        sheet,
        skip_rows: headerRow,
        columns: included.map((c) => ({
          index: c.index,
          name: c.name,
          type: c.type,
          label: c.header !== c.name ? c.header : null,
        })),
      };
      if (table) {
        await TablesService.importCsvIntoTable({
          path: { group_id: spotGroup.id, table_id: table.id },
          body: { file, spec: JSON.stringify(spec) },
        });
        return { tableId: table.id, inserted: null };
      }
      const res = await TablesService.importCsv({
        path: { group_id: spotGroup.id },
        body: {
          file,
          spec: JSON.stringify({ ...spec, id_type: idType }),
        },
      });
      return { tableId: res.data.id, inserted: null };
    },
    onSuccess: ({ tableId, inserted }) => {
      queryClient.invalidateQueries({ queryKey: [DATA_KEYS.TABLES] });
      onOpenChange(false);
      SpotToast.success(
        inserted === null
          ? "File imported successfully"
          : `${inserted} ${inserted === 1 ? "row" : "rows"} imported`
      );
      if (table) {
        queryClient.invalidateQueries({ queryKey: [DATA_KEYS.TABLES_ID] });
        queryClient.invalidateQueries({ queryKey: [DATA_KEYS.TABLE_ROWS] });
        onImported?.();
      } else {
        router.push(`/tables/${tableId}`);
      }
    },
    onError: (e: unknown) => {
      SpotToast.error(extractErrorMessage(e, "Import failed"));
      setProgress(null);
    },
  });

  const setColumn = (index: number, patch: Partial<DetectedColumn>) =>
    setColumns((cols) =>
      cols.map((c) => (c.index === index ? { ...c, ...patch } : c))
    );

  // Per-column errors keyed by CSV index: reserved "id", malformed identifiers,
  // and duplicate identifiers among the columns actually being imported.
  const errors = useMemo(() => {
    const errs = new Map<number, string>();
    const counts = new Map<string, number>();
    columns
      .filter((c) => c.include)
      .forEach((c) => counts.set(c.name, (counts.get(c.name) ?? 0) + 1));
    columns.forEach((c) => {
      if (!c.include) return;
      if (c.name === "id")
        errs.set(c.index, "“id” is reserved. Rename it to import this column.");
      else if (!IDENTIFIER_RE.test(c.name))
        errs.set(
          c.index,
          "Use lowercase letters, numbers and underscores; start with a letter."
        );
      else if ((counts.get(c.name) ?? 0) > 1)
        errs.set(
          c.index,
          "Duplicate identifier. Each column needs a unique name."
        );
    });
    return errs;
  }, [columns]);

  const { data: existingTables } = useRemoteData(
    DATA_KEYS.TABLES,
    { path: { group_id: spotGroup.id } },
    { enabled: isOpen }
  );
  const nameTaken =
    tableName.trim().length > 0 &&
    (existingTables ?? []).some(
      (t) =>
        t.id !== table?.id &&
        t.name.trim().toLowerCase() === tableName.trim().toLowerCase()
    );

  const includedCount = columns.filter((c) => c.include).length;
  const isImporting = useDialogMutationLoading(isOpen, importMutation);
  const canImport = appendMode
    ? mapped.length > 0 && !previewLoading
    : tableName.trim().length > 0 &&
      !nameTaken &&
      includedCount > 0 &&
      errors.size === 0 &&
      !previewLoading;

  return (
    <BaseDialog
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!importMutation.isPending) onOpenChange(open);
      }}
      title={
        appendMode && table ? `Import rows into "${table.name}"` : "Import data"
      }
      description={
        parseError
          ? "Could not read this file."
          : previewLoading
            ? "Reading file…"
            : `${totalRows !== null ? Math.max(0, totalRows - 1 - headerRow) : rows.length}${totalRows === null && previewTruncated ? "+" : ""} rows · ${columns.length} columns detected`
      }
      size="lg"
      footerActions={[
        {
          label: "Cancel",
          props: {
            variant: "outline",
            onClick: () => onOpenChange(false),
            disabled: isImporting,
          },
        },
        {
          label: progress ?? "Import",
          props: {
            onClick: () => importMutation.mutate(),
            disabled: !canImport,
            loading: isImporting,
          },
        },
      ]}
    >
      <div className="flex flex-col gap-5 py-2">
        {parseError ? (
          <p className="text-sm text-destructive">{parseError}</p>
        ) : (
          <>
            {!appendMode && (
              <div className="flex flex-col gap-1.5">
                <NoxusInput
                  id="import-table-name"
                  name="import-table-name"
                  label="Table name"
                  value={tableName}
                  onChange={(e) => setNameEdit(e.target.value)}
                  placeholder="My table"
                  maxLength={MAX_ENTITY_NAME}
                  disable={importMutation.isPending}
                  hasError={nameTaken}
                />
                {nameTaken && (
                  <p className="text-xs text-destructive">
                    A table with this name already exists.
                  </p>
                )}
              </div>
            )}

            {!table && (
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="import-id-type">Primary key</Label>
                <Select
                  value={idType}
                  onValueChange={(v) =>
                    setIdType(v as TableResponse["id_type"])
                  }
                  disabled={importMutation.isPending}
                >
                  <SelectTrigger id="import-id-type" className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="uuid">UUID (random)</SelectItem>
                    <SelectItem value="autoincrement">
                      Auto-increment (1, 2, 3…)
                    </SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  How each row&apos;s identifier is generated. This can&apos;t
                  be changed later.
                </p>
              </div>
            )}

            {allRows.length > 1 && (
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="import-header-row">Header row</Label>
                <Select
                  value={String(headerRow)}
                  onValueChange={(v) => {
                    setHeaderRow(Number(v));
                    setMappingOverrides({});
                  }}
                  disabled={importMutation.isPending || previewLoading}
                >
                  <SelectTrigger id="import-header-row" className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="max-w-(--radix-select-trigger-width)">
                    {allRows.slice(0, HEADER_ROW_CANDIDATES).map((row, i) => {
                      const cells = row.filter((c) => c.trim() !== "");
                      const label =
                        cells.slice(0, 4).join(", ").slice(0, 60) || "(empty)";
                      return (
                        <SelectItem
                          key={i}
                          value={String(i)}
                          className="*:[span]:last:min-w-0"
                        >
                          <span className="truncate">
                            Row {i + 1}: {label}
                          </span>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
            )}

            {sheets.length > 1 && (
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="import-sheet">Sheet</Label>
                <Select
                  value={sheet ?? sheets[0]}
                  onValueChange={(v) => {
                    setSheet(v);
                    setMappingOverrides({});
                  }}
                  disabled={importMutation.isPending || previewLoading}
                >
                  <SelectTrigger id="import-sheet" className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {sheets.map((s) => (
                      <SelectItem key={s} value={s}>
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {!(file && isXlsx(file)) && (
              <div className="grid grid-cols-2 gap-3">
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="import-delimiter">Delimiter</Label>
                  <Select
                    value={delimiter}
                    onValueChange={(v) => {
                      setDelimiter(v as Delimiter);
                      setMappingOverrides({});
                    }}
                    disabled={importMutation.isPending}
                  >
                    <SelectTrigger id="import-delimiter" className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value=",">Comma</SelectItem>
                      <SelectItem value=";">Semicolon</SelectItem>
                      <SelectItem value={"\t"}>Tab</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="import-encoding">Encoding</Label>
                  <Select
                    value={encoding}
                    onValueChange={(v) => {
                      setEncoding(v as Encoding);
                      setMappingOverrides({});
                    }}
                    disabled={importMutation.isPending}
                  >
                    <SelectTrigger id="import-encoding" className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="utf-8">UTF-8</SelectItem>
                      <SelectItem value="latin-1">Latin-1 (Windows)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {previewLoading ? (
              <div className="flex flex-col gap-1.5">
                <Label>Columns to import</Label>
                <div className="divide-y divide-border rounded-md border">
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="flex items-center gap-3 px-3 py-3">
                      <Skeleton className="size-4 rounded" />
                      <Skeleton className="h-4 flex-1" />
                      <Skeleton className="h-6 w-44" />
                    </div>
                  ))}
                </div>
              </div>
            ) : appendMode && table ? (
              <div className="flex flex-col gap-1.5">
                <div className="flex items-baseline justify-between">
                  <Label>Fill table columns</Label>
                  <span className="text-xs text-muted-foreground">
                    {mapped.length} of {table.columns.length} filled
                  </span>
                </div>
                <div className="divide-y divide-border rounded-md border">
                  {table.columns.map((tc) => {
                    const source = mapping[tc.name];
                    return (
                      <div
                        key={tc.name}
                        className="flex items-center gap-3 px-3 py-2 text-sm"
                      >
                        <span className="flex-1 truncate font-medium">
                          {tc.label ?? tc.name}
                        </span>
                        <span className="w-16 shrink-0 text-xs capitalize text-muted-foreground">
                          {tc.type}
                        </span>
                        <Select
                          value={
                            source === null || source === undefined
                              ? NOT_IMPORTED
                              : String(source)
                          }
                          onValueChange={(v) =>
                            setMappingOverrides((o) => ({
                              ...o,
                              [tc.name]: v === NOT_IMPORTED ? null : Number(v),
                            }))
                          }
                          disabled={importMutation.isPending}
                        >
                          <SelectTrigger
                            className="h-7 w-44 text-xs"
                            aria-label={`CSV column for ${tc.label ?? tc.name}`}
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value={NOT_IMPORTED}>
                              Don't import
                            </SelectItem>
                            {columns.map((c) => (
                              <SelectItem key={c.index} value={String(c.index)}>
                                {c.header || `Column ${c.index + 1}`}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="flex flex-col gap-1.5">
                <Label>Columns to import</Label>
                <div className="flex items-baseline justify-between text-xs text-muted-foreground">
                  <span>Column types can be changed after import.</span>
                  <span>
                    {includedCount} of {columns.length} selected
                  </span>
                </div>
                <div className="divide-y divide-border rounded-md border">
                  {columns.map((col) => {
                    const Icon = TYPE_ICONS[col.type];
                    const error = errors.get(col.index);
                    return (
                      <div key={col.index} className="px-3 py-2">
                        <div className="flex items-center gap-3 text-sm">
                          <Checkbox
                            checked={col.include}
                            onCheckedChange={(v) =>
                              setColumn(col.index, { include: v === true })
                            }
                            disabled={importMutation.isPending}
                            aria-label={`Import column ${col.header}`}
                          />
                          <Icon
                            className={cn(
                              "size-3.5 shrink-0 text-muted-foreground",
                              !col.include && "opacity-40"
                            )}
                          />
                          <span
                            className={cn(
                              "flex-1 truncate font-medium",
                              !col.include &&
                                "text-muted-foreground line-through"
                            )}
                          >
                            {col.header}
                          </span>
                          <Input
                            value={col.name}
                            onChange={(e) =>
                              setColumn(col.index, { name: e.target.value })
                            }
                            disabled={!col.include || importMutation.isPending}
                            aria-invalid={!!error}
                            className="h-7 w-44 font-mono text-xs"
                          />
                          <span className="w-14 shrink-0 text-right text-xs capitalize text-muted-foreground">
                            {col.type}
                          </span>
                        </div>
                        {error && (
                          <p className="ml-7 mt-1 text-xs text-destructive">
                            {error}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </BaseDialog>
  );
}
